<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import type {
    BetaCreatePageKind,
    BetaCreatePageSelection,
    BetaPageCombination,
    BetaPageCombinationGroups,
} from '@/types/beta-dashboard'
import {
    betaCreatePageAutomaticCombinations,
    betaCreatePageLayoutSteps,
} from '@/utils/beta-page-creation'

type WizardStage = 'type' | 'layout' | 'details'

interface PageKindOption {
    id: BetaCreatePageKind
    title: string
    description: string
    idealFor: string
    result: string
    iconPaths: string[]
}

const props = withDefaults(defineProps<{
    open: boolean
    loading?: boolean
    existingNames: string[]
    combinations?: BetaPageCombinationGroups
    unavailableKinds?: BetaCreatePageKind[]
    initialKind?: BetaCreatePageKind
}>(), {
    combinations: () => ({}),
    unavailableKinds: () => [],
})

const emit = defineEmits<{
    close: []
    create: [payload: BetaCreatePageSelection]
}>()

const kinds: PageKindOption[] = [
    {
        id: 'text',
        title: 'Página em branco',
        description: 'Comece com uma estrutura flexível para combinar textos, imagens e chamadas.',
        idealFor: 'Landing pages, serviços e conteúdos personalizados.',
        result: 'Uma página única pronta para editar livremente.',
        iconPaths: [
            'M7.5 3.75h6l3 3v13.5h-9V3.75Z',
            'M13.5 3.75v3h3',
            'M9.75 11.25h4.5M9.75 14.25h4.5M9.75 17.25h3',
        ],
    },
    {
        id: 'blog',
        title: 'Blog',
        description: 'Publique artigos e novidades em uma listagem organizada por data.',
        idealFor: 'Notícias, artigos, diário e conteúdo recorrente.',
        result: 'Uma listagem e um modelo para cada publicação.',
        iconPaths: [
            'M4.5 5.25h15v13.5h-15V5.25Z',
            'M7.5 8.25h9M7.5 11.25h6M7.5 14.25h7.5M7.5 17.25h4.5',
        ],
    },
    {
        id: 'portfolio',
        title: 'Galerias diversas',
        description: 'Organize vários projetos ou álbuns, cada um com sua própria página.',
        idealFor: 'Portfólios, eventos, ensaios e coleções.',
        result: 'Uma vitrine de projetos e um layout interno para cada item.',
        iconPaths: [
            'M4.5 4.5h6v6h-6v-6ZM13.5 4.5h6v6h-6v-6ZM4.5 13.5h6v6h-6v-6ZM13.5 13.5h6v6h-6v-6Z',
        ],
    },
    {
        id: 'gallery',
        title: 'Galeria única',
        description: 'Apresente uma coleção de imagens dentro de uma única página.',
        idealFor: 'Fotos, produtos, trabalhos e catálogos compactos.',
        result: 'Uma página visual com galeria pronta para receber imagens.',
        iconPaths: [
            'M4.5 5.25h15v13.5h-15V5.25Z',
            'm6.75 16.5 3.75-4.5 2.25 2.25 1.5-1.5 3 3.75',
            'M15.75 9a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Z',
        ],
    },
    {
        id: 'about',
        title: 'Sobre',
        description: 'Conte sua história ou apresente sua empresa com imagem e texto.',
        idealFor: 'Biografia, apresentação de marca e equipe.',
        result: 'Uma página institucional com estrutura de apresentação.',
        iconPaths: [
            'M12 12a3.75 3.75 0 1 0 0-7.5 3.75 3.75 0 0 0 0 7.5Z',
            'M5.25 20.25a6.75 6.75 0 0 1 13.5 0',
        ],
    },
    {
        id: 'contact',
        title: 'Formulário',
        description: 'Receba mensagens, pedidos de orçamento e dados de novos contatos.',
        idealFor: 'Atendimento, orçamento e captação de clientes.',
        result: 'Uma página com formulário de contato configurável.',
        iconPaths: [
            'M3.75 6.75h16.5v10.5H3.75V6.75Z',
            'm4.5 7.5 7.5 5.25 7.5-5.25',
        ],
    },
    {
        id: 'testimonials',
        title: 'Depoimentos',
        description: 'Reúna avaliações e histórias de clientes em uma página dedicada.',
        idealFor: 'Prova social, recomendações e casos de sucesso.',
        result: 'Uma página preparada para cadastrar vários depoimentos.',
        iconPaths: [
            'M4.5 5.25h10.5v8.25H9l-3.75 3v-3H4.5V5.25Z',
            'M16.5 8.25h3v8.25h-.75v3l-3.75-3h-3v-1.5',
        ],
    },
    {
        id: 'newsletter',
        title: 'Newsletter',
        description: 'Crie um ponto de inscrição para manter sua audiência por perto.',
        idealFor: 'Lista de e-mails, lançamentos e novidades.',
        result: 'Uma página com chamada e formulário de cadastro.',
        iconPaths: [
            'M3.75 5.25h16.5v13.5H3.75V5.25Z',
            'm4.5 6 7.5 6 7.5-6',
            'M7.5 15.75h4.5',
        ],
    },
    {
        id: 'store',
        title: 'Loja',
        description: 'Mostre produtos em uma vitrine organizada e pronta para crescer.',
        idealFor: 'Produtos, serviços, cursos e catálogos.',
        result: 'Uma página de listagem com estrutura de loja.',
        iconPaths: [
            'M5.25 8.25h13.5l-.75 12H6l-.75-12Z',
            'M8.25 9V6.75a3.75 3.75 0 0 1 7.5 0V9',
        ],
    },
    {
        id: 'link',
        title: 'Link externo',
        description: 'Adicione ao menu um atalho para outro site ou endereço da internet.',
        idealFor: 'Redes sociais, lojas externas e áreas de membros.',
        result: 'Um item de menu que direciona o visitante para outro endereço.',
        iconPaths: [
            'M10.5 13.5 13.5 10.5',
            'M8.25 15.75 6.75 17.25a3.182 3.182 0 0 1-4.5-4.5l3-3a3.182 3.182 0 0 1 4.5 0',
            'm15.75 8.25 1.5-1.5a3.182 3.182 0 0 1 4.5 4.5l-3 3a3.182 3.182 0 0 1-4.5 0',
        ],
    },
]

const stage = ref<WizardStage>('type')
const pageName = ref('')
const selectedKind = ref<BetaCreatePageKind | null>(null)
const layoutStepIndex = ref(0)
const selectedCombinations = ref<Array<BetaPageCombination | null>>([])

const firstAvailableKind = () => (
    kinds.find((kind) => !props.unavailableKinds.includes(kind.id))?.id ?? null
)

const selectedKindData = computed(() => (
    kinds.find((kind) => kind.id === selectedKind.value) ?? null
))
const layoutSteps = computed(() => (
    selectedKind.value
        ? betaCreatePageLayoutSteps(selectedKind.value, props.combinations)
        : []
))
const activeLayoutStep = computed(() => layoutSteps.value[layoutStepIndex.value] ?? null)
const automaticCombinations = computed(() => (
    selectedKind.value
        ? betaCreatePageAutomaticCombinations(selectedKind.value, props.combinations)
        : []
))
const creationCombinations = computed(() => {
    if (automaticCombinations.value.length) return automaticCombinations.value

    return selectedCombinations.value.filter(
        (combination): combination is BetaPageCombination => Boolean(combination),
    )
})
const normalizedName = computed(() => pageName.value.trim().toLocaleLowerCase())
const nameAlreadyExists = computed(() => props.existingNames.some((name) => (
    String(name).trim().toLocaleLowerCase() === normalizedName.value
)))
const selectedKindUnavailable = computed(() => (
    selectedKind.value ? props.unavailableKinds.includes(selectedKind.value) : false
))
const layoutSelected = computed(() => {
    const id = selectedCombinations.value[layoutStepIndex.value]?.id
    return id !== undefined && id !== null
})
const totalSteps = computed(() => selectedKind.value
    ? layoutSteps.value.length + 2
    : 2)
const currentStep = computed(() => {
    if (stage.value === 'type') return 1
    if (stage.value === 'layout') return layoutStepIndex.value + 2
    return totalSteps.value
})
const wizardSteps = computed(() => [
    { id: 'type', label: 'Tipo de página' },
    ...layoutSteps.value.map((step) => ({
        id: step.id,
        label: step.id === 'listing'
            ? 'Listagem'
            : step.id === 'content'
                ? 'Página interna'
                : 'Layout',
    })),
    { id: 'details', label: 'Nome e criação' },
])
const heading = computed(() => {
    if (stage.value === 'type') return 'Que tipo de página você precisa?'
    if (stage.value === 'layout') return activeLayoutStep.value?.title ?? 'Escolha o layout'
    return 'Dê um nome à sua página'
})
const stageDescription = computed(() => {
    if (stage.value === 'type') {
        if (selectedKind.value === 'text') {
            return 'A estrutura de página única será aplicada automaticamente, sem escolher um layout.'
        }

        if (selectedKind.value === 'link') {
            return 'Este tipo não usa layout. Depois de criar, você informa o endereço de destino.'
        }

        return 'Escolha pelo objetivo. Na próxima etapa você define a aparência inicial.'
    }

    if (stage.value === 'layout') {
        return activeLayoutStep.value?.description
            ?? 'Selecione uma prévia para definir a estrutura inicial.'
    }

    return selectedKind.value === 'link'
        ? 'Use um nome fácil de reconhecer no menu. O endereço será configurado em seguida.'
        : 'Esse será o nome exibido no painel e poderá ser alterado depois.'
})
const footerHint = computed(() => {
    if (stage.value === 'type') {
        if (selectedKind.value === 'text') {
            return 'A página em branco será criada com o modelo único padrão.'
        }
        if (selectedKind.value === 'link') {
            return 'Links externos não possuem layout ou conteúdo próprio.'
        }
        return 'Você poderá trocar o layout e editar todo o conteúdo depois.'
    }
    if (stage.value === 'layout') {
        return layoutSelected.value
            ? 'Layout selecionado. Continue quando estiver pronto.'
            : 'Selecione um layout para continuar.'
    }
    if (nameAlreadyExists.value) return 'Escolha um nome diferente para criar a página.'
    return 'A página será criada como rascunho para você revisar.'
})
const canContinue = computed(() => {
    if (props.loading) return false
    if (stage.value === 'type') return Boolean(selectedKind.value) && !selectedKindUnavailable.value
    if (stage.value === 'layout') return layoutSelected.value
    if (selectedKind.value === 'text' && !automaticCombinations.value.length) return false
    return Boolean(normalizedName.value) && !nameAlreadyExists.value
})

const reset = () => {
    stage.value = 'type'
    pageName.value = ''
    selectedKind.value = firstAvailableKind()
    layoutStepIndex.value = 0
    selectedCombinations.value = []
}

const selectKind = (kind: BetaCreatePageKind) => {
    if (props.unavailableKinds.includes(kind)) return
    if (selectedKind.value !== kind) selectedCombinations.value = []
    selectedKind.value = kind
    layoutStepIndex.value = 0
    stage.value = 'type'
}

const selectCombination = (combination: BetaPageCombination) => {
    selectedCombinations.value[layoutStepIndex.value] = combination
    selectedCombinations.value = [...selectedCombinations.value]
}

const continueFlow = () => {
    if (!canContinue.value || !selectedKind.value) return

    if (stage.value === 'type') {
        stage.value = layoutSteps.value.length ? 'layout' : 'details'
        return
    }

    if (stage.value === 'layout' && layoutStepIndex.value < layoutSteps.value.length - 1) {
        layoutStepIndex.value += 1
        return
    }

    stage.value = 'details'
}

const previous = () => {
    if (props.loading) return

    if (stage.value === 'details' && layoutSteps.value.length) {
        stage.value = 'layout'
        layoutStepIndex.value = layoutSteps.value.length - 1
        return
    }

    if (stage.value === 'layout' && layoutStepIndex.value > 0) {
        layoutStepIndex.value -= 1
        return
    }

    stage.value = 'type'
}

const close = () => {
    if (props.loading) return
    emit('close')
}

const submit = () => {
    if (stage.value !== 'details' || !canContinue.value || !selectedKind.value) return

    emit('create', {
        name: pageName.value.trim(),
        kind: selectedKind.value,
        combinations: creationCombinations.value,
    })
}

watch(
    () => [props.open, props.initialKind] as const,
    ([open, initialKind]) => {
        reset()
        if (!open || !initialKind || !kinds.some((kind) => kind.id === initialKind)) return

        selectedKind.value = initialKind
        stage.value = betaCreatePageLayoutSteps(initialKind, props.combinations).length
            ? 'layout'
            : 'details'
    },
    { immediate: true },
)
</script>

<template>
    <Teleport to="body">
        <div
            v-if="props.open"
            class="beta-create-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="beta-create-dialog-title"
            @click.self="close"
        >
            <form class="beta-create-dialog__panel" @submit.prevent="submit">
                <header class="beta-create-dialog__header">
                    <div class="beta-create-dialog__header-title">
                        <span class="beta-create-dialog__header-icon" aria-hidden="true">
                            <svg viewBox="0 0 24 24" fill="none">
                                <path d="M7 3.75h7l3 3v13.5H7V3.75Z" />
                                <path d="M14 3.75v3h3M9.75 11h4.5M9.75 14h4.5" />
                                <path d="M18.5 12.25v6M15.5 15.25h6" />
                            </svg>
                        </span>
                        <span>
                            <small>Novo conteúdo</small>
                            <h2 id="beta-create-dialog-title">Criador de páginas</h2>
                        </span>
                    </div>

                    <div class="beta-create-dialog__header-copy">
                        <strong>{{ heading }}</strong>
                        <small>Etapa {{ currentStep }} de {{ totalSteps }}</small>
                    </div>

                    <button
                        type="button"
                        class="beta-create-dialog__close"
                        aria-label="Fechar criador de páginas"
                        :disabled="props.loading"
                        @click="close"
                    >
                        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                            <path d="m7 7 10 10M17 7 7 17" />
                        </svg>
                    </button>
                </header>

                <nav class="beta-create-dialog__progress" aria-label="Progresso da criação">
                    <ol>
                        <li
                            v-for="(stepItem, index) in wizardSteps"
                            :key="`${stepItem.id}-${index}`"
                            :class="{
                                'is-active': index + 1 === currentStep,
                                'is-complete': index + 1 < currentStep,
                            }"
                        >
                            <span aria-hidden="true">
                                <svg v-if="index + 1 < currentStep" viewBox="0 0 16 16" fill="none">
                                    <path d="m3.5 8 3 3 6-6" />
                                </svg>
                                <template v-else>{{ index + 1 }}</template>
                            </span>
                            <small>{{ stepItem.label }}</small>
                        </li>
                    </ol>
                </nav>

                <main class="beta-create-dialog__body">
                    <nav class="beta-create-dialog__type-tabs" aria-label="Tipos de página">
                        <button
                            v-for="kind in kinds"
                            :key="kind.id"
                            type="button"
                            :class="[
                                'beta-create-dialog__type-tab',
                                selectedKind === kind.id && 'is-active',
                            ]"
                            :disabled="props.unavailableKinds.includes(kind.id)"
                            :aria-pressed="selectedKind === kind.id"
                            @click="selectKind(kind.id)"
                        >
                            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                <path
                                    v-for="path in kind.iconPaths"
                                    :key="path"
                                    :d="path"
                                />
                            </svg>
                            <span>{{ kind.title }}</span>
                            <small v-if="props.unavailableKinds.includes(kind.id)">Indisponível</small>
                        </button>
                    </nav>

                    <section class="beta-create-dialog__stage">
                        <header class="beta-create-dialog__stage-header">
                            <div>
                                <small>Etapa {{ currentStep }}</small>
                                <h3>{{ heading }}</h3>
                                <p>{{ stageDescription }}</p>
                            </div>
                            <span v-if="selectedKindData" class="beta-create-dialog__selected-kind">
                                {{ selectedKindData.title }}
                            </span>
                        </header>

                        <div v-if="stage === 'type' && selectedKindData" class="beta-create-dialog__type-guide">
                            <div class="beta-create-dialog__type-guide-copy">
                                <span class="beta-create-dialog__type-guide-icon" aria-hidden="true">
                                    <svg viewBox="0 0 24 24" fill="none">
                                        <path
                                            v-for="path in selectedKindData.iconPaths"
                                            :key="path"
                                            :d="path"
                                        />
                                    </svg>
                                </span>
                                <div>
                                    <small>Você escolheu</small>
                                    <h4>{{ selectedKindData.title }}</h4>
                                    <p>{{ selectedKindData.description }}</p>
                                </div>

                                <dl>
                                    <div>
                                        <dt>Ideal para</dt>
                                        <dd>{{ selectedKindData.idealFor }}</dd>
                                    </div>
                                    <div>
                                        <dt>O que será criado</dt>
                                        <dd>{{ selectedKindData.result }}</dd>
                                    </div>
                                </dl>
                            </div>

                            <div
                                :class="[
                                    'beta-create-dialog__concept-preview',
                                    `is-${selectedKindData.id}`,
                                ]"
                                aria-hidden="true"
                            >
                                <div class="beta-create-dialog__browser-bar">
                                    <i /><i /><i />
                                    <span />
                                </div>

                                <template v-if="['blog', 'portfolio', 'store'].includes(selectedKindData.id)">
                                    <span class="beta-create-dialog__mock-title" />
                                    <div class="beta-create-dialog__mock-grid">
                                        <i v-for="item in 6" :key="item" />
                                    </div>
                                </template>

                                <template v-else-if="selectedKindData.id === 'gallery'">
                                    <span class="beta-create-dialog__mock-title" />
                                    <div class="beta-create-dialog__mock-gallery">
                                        <i v-for="item in 5" :key="item" />
                                    </div>
                                </template>

                                <template v-else-if="['contact', 'newsletter'].includes(selectedKindData.id)">
                                    <span class="beta-create-dialog__mock-title" />
                                    <div class="beta-create-dialog__mock-form">
                                        <i /><i /><i />
                                        <b />
                                    </div>
                                </template>

                                <template v-else-if="selectedKindData.id === 'testimonials'">
                                    <span class="beta-create-dialog__mock-title" />
                                    <div class="beta-create-dialog__mock-quotes">
                                        <i v-for="item in 3" :key="item" />
                                    </div>
                                </template>

                                <template v-else-if="selectedKindData.id === 'about'">
                                    <div class="beta-create-dialog__mock-about">
                                        <i />
                                        <span><b /><b /><b /><b /></span>
                                    </div>
                                </template>

                                <template v-else-if="selectedKindData.id === 'link'">
                                    <div class="beta-create-dialog__mock-link">
                                        <svg viewBox="0 0 24 24" fill="none">
                                            <path d="M10.5 13.5 13.5 10.5" />
                                            <path d="M8.25 15.75 6.75 17.25a3.182 3.182 0 0 1-4.5-4.5l3-3a3.182 3.182 0 0 1 4.5 0" />
                                            <path d="m15.75 8.25 1.5-1.5a3.182 3.182 0 0 1 4.5 4.5l-3 3a3.182 3.182 0 0 1-4.5 0" />
                                        </svg>
                                        <span />
                                    </div>
                                </template>

                                <template v-else>
                                    <div class="beta-create-dialog__mock-hero">
                                        <span><b /><b /><b /></span>
                                        <i />
                                    </div>
                                    <div class="beta-create-dialog__mock-copy"><i /><i /><i /></div>
                                </template>
                            </div>
                        </div>

                        <div v-else-if="stage === 'layout'" class="beta-create-dialog__layouts">
                            <div v-if="activeLayoutStep?.combinations.length" class="beta-create-dialog__layout-grid">
                                <button
                                    v-for="(combination, index) in activeLayoutStep.combinations"
                                    :key="combination.id"
                                    type="button"
                                    :class="[
                                        'beta-create-dialog__layout',
                                        selectedCombinations[layoutStepIndex]?.id === combination.id && 'is-active',
                                    ]"
                                    :aria-pressed="selectedCombinations[layoutStepIndex]?.id === combination.id"
                                    @click="selectCombination(combination)"
                                >
                                    <span class="beta-create-dialog__layout-preview">
                                        <img
                                            v-if="combination.picture"
                                            :src="combination.picture"
                                            :alt="combination.name || combination.description || `Layout ${index + 1}`"
                                        >
                                        <span v-else class="beta-create-dialog__layout-placeholder" aria-hidden="true">
                                            <i /><i /><i /><i />
                                        </span>
                                        <span
                                            v-if="selectedCombinations[layoutStepIndex]?.id === combination.id"
                                            class="beta-create-dialog__layout-check"
                                            aria-hidden="true"
                                        >
                                            <svg viewBox="0 0 16 16" fill="none">
                                                <path d="m3.5 8 3 3 6-6" />
                                            </svg>
                                        </span>
                                    </span>
                                    <span class="beta-create-dialog__layout-copy">
                                        <span>
                                            <small>Modelo {{ String(index + 1).padStart(2, '0') }}</small>
                                            <!-- <strong>
                                                {{ combination.name || combination.description || `Layout ${index + 1}` }}
                                            </strong> -->
                                        </span>
                                        <em>
                                            {{ selectedCombinations[layoutStepIndex]?.id === combination.id
                                                ? 'Selecionado'
                                                : 'Escolher' }}
                                        </em>
                                    </span>
                                </button>
                            </div>
                            <div v-else class="beta-create-dialog__empty-layouts">
                                <span aria-hidden="true">!</span>
                                <strong>Nenhum modelo disponível</strong>
                                <small>Feche o criador e tente novamente em alguns instantes.</small>
                            </div>
                        </div>

                        <div v-else class="beta-create-dialog__details">
                            <div class="beta-create-dialog__details-form">
                                <label class="beta-create-dialog__field">
                                    <span>Nome da página</span>
                                    <input
                                        v-model="pageName"
                                        type="text"
                                        maxlength="80"
                                        :placeholder="selectedKind === 'link'
                                            ? 'Ex.: Área do cliente'
                                            : 'Ex.: Casamentos, Sobre mim, Novidades'"
                                        autofocus
                                    >
                                    <small v-if="nameAlreadyExists" class="is-error">
                                        Já existe uma página com esse nome.
                                    </small>
                                </label>

                                <div class="beta-create-dialog__after-create">
                                    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                        <path d="M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z" />
                                        <path d="M12 10.5v5.25M12 7.5h.008" />
                                    </svg>
                                    <span>
                                        <strong>O que acontece depois?</strong>
                                        <small v-if="selectedKind === 'link'">
                                            Você será levado à configuração para informar o endereço de destino.
                                        </small>
                                        <small v-else>
                                            A nova página será aberta para você personalizar textos, imagens e configurações.
                                        </small>
                                    </span>
                                </div>
                            </div>

                            <aside class="beta-create-dialog__summary">
                                <span class="beta-create-dialog__summary-label">Resumo da página</span>
                                <div class="beta-create-dialog__summary-kind">
                                    <span class="beta-create-dialog__type-guide-icon" aria-hidden="true">
                                        <svg viewBox="0 0 24 24" fill="none">
                                            <path
                                                v-for="path in selectedKindData?.iconPaths"
                                                :key="path"
                                                :d="path"
                                            />
                                        </svg>
                                    </span>
                                    <span>
                                        <small>Tipo</small>
                                        <strong>{{ selectedKindData?.title }}</strong>
                                    </span>
                                </div>
                                <div
                                    v-if="automaticCombinations.length"
                                    class="beta-create-dialog__summary-layout"
                                >
                                    <span class="beta-create-dialog__summary-preview">
                                        <img
                                            v-if="automaticCombinations[0]?.picture"
                                            :src="automaticCombinations[0].picture"
                                            alt=""
                                        >
                                        <span v-else aria-hidden="true">Aa</span>
                                    </span>
                                    <span>
                                        <small>Estrutura automática</small>
                                        <strong>Página única em branco</strong>
                                    </span>
                                </div>
                                <div
                                    v-for="(combination, index) in selectedCombinations"
                                    :key="combination?.id"
                                    class="beta-create-dialog__summary-layout"
                                >
                                    <span class="beta-create-dialog__summary-preview">
                                        <img v-if="combination?.picture" :src="combination.picture" alt="">
                                        <span v-else aria-hidden="true">Aa</span>
                                    </span>
                                    <span>
                                        <small>{{ layoutSteps[index]?.title }}</small>
                                        <strong>
                                            {{ combination?.name || combination?.description || `Layout ${index + 1}` }}
                                        </strong>
                                    </span>
                                </div>
                            </aside>
                        </div>
                    </section>
                </main>

                <footer class="beta-create-dialog__footer">
                    <p>{{ footerHint }}</p>
                    <div>
                        <button
                            v-if="stage === 'type'"
                            type="button"
                            class="beta-create-dialog__cancel"
                            @click="close"
                        >
                            Cancelar
                        </button>
                        <button
                            v-else
                            type="button"
                            class="beta-create-dialog__cancel"
                            :disabled="props.loading"
                            @click="previous"
                        >
                            Voltar
                        </button>

                        <button
                            v-if="stage !== 'details'"
                            type="button"
                            class="beta-create-dialog__submit"
                            :disabled="!canContinue"
                            @click="continueFlow"
                        >
                            Continuar
                            <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
                                <path d="m6 3.5 4.5 4.5L6 12.5" />
                            </svg>
                        </button>
                        <button
                            v-else
                            type="submit"
                            class="beta-create-dialog__submit"
                            :disabled="!canContinue"
                        >
                            {{ props.loading ? 'Criando…' : 'Criar página' }}
                        </button>
                    </div>
                </footer>
            </form>
        </div>
    </Teleport>
</template>

<style scoped>
.beta-create-dialog {
    position: fixed;
    inset: 0;
    z-index: 100;
    display: grid;
    place-items: center;
    overflow-y: auto;
    background: rgba(13, 29, 51, 0.62);
    padding: 16px;
    backdrop-filter: blur(8px);
}

.beta-create-dialog__panel {
    display: grid;
    width: min(1240px, 100%);
    height: min(820px, calc(100vh - 32px));
    grid-template-rows: auto auto minmax(0, 1fr) auto;
    overflow: hidden;
    border: 1px solid #dbe3ed;
    border-radius: 24px;
    background: #fff;
}

.beta-create-dialog__header {
    display: grid;
    grid-template-columns: minmax(230px, 1fr) minmax(280px, auto) minmax(230px, 1fr);
    align-items: center;
    gap: 24px;
    border-bottom: 1px solid #e8edf3;
    padding: 20px 28px;
}

.beta-create-dialog__header-title {
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 12px;
}

.beta-create-dialog__header-icon,
.beta-create-dialog__type-guide-icon {
    display: grid;
    width: 42px;
    height: 42px;
    flex: none;
    place-items: center;
    border-radius: 13px;
    background: color-mix(in srgb, var(--main-color) 10%, white);
    color: var(--main-color);
}

.beta-create-dialog__header-icon svg,
.beta-create-dialog__type-guide-icon svg {
    width: 22px;
    height: 22px;
    stroke: currentColor;
    stroke-width: 1.6;
    stroke-linecap: round;
    stroke-linejoin: round;
}

.beta-create-dialog__header-title small,
.beta-create-dialog__header-title h2 {
    display: block;
}

.beta-create-dialog__header-title small {
    color: var(--main-color);
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
}

.beta-create-dialog__header-title h2 {
    margin-top: 2px;
    color: #10233e;
    font-size: 21px;
    font-weight: 700;
    letter-spacing: -0.025em;
}

.beta-create-dialog__header-copy {
    min-width: 0;
    text-align: center;
}

.beta-create-dialog__header-copy strong,
.beta-create-dialog__header-copy small {
    display: block;
}

.beta-create-dialog__header-copy strong {
    overflow: hidden;
    color: #3a506d;
    font-size: 12px;
    font-weight: 700;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.beta-create-dialog__header-copy small {
    margin-top: 3px;
    color: #8796aa;
    font-size: 10px;
}

.beta-create-dialog__close {
    display: grid;
    width: 40px;
    height: 40px;
    justify-self: end;
    place-items: center;
    border: 1px solid #e1e7ef;
    border-radius: 12px;
    background: #f6f8fb;
    color: #61738c;
}

.beta-create-dialog__close svg {
    width: 19px;
    height: 19px;
    stroke: currentColor;
    stroke-width: 1.7;
    stroke-linecap: round;
}

.beta-create-dialog__progress {
    border-bottom: 1px solid #edf1f5;
    background: #fbfcfe;
    padding: 12px 28px;
}

.beta-create-dialog__progress ol {
    display: flex;
    max-width: 680px;
    align-items: center;
    justify-content: center;
    gap: 0;
    margin: 0 auto;
}

.beta-create-dialog__progress li {
    position: relative;
    display: flex;
    min-width: 0;
    flex: 1;
    align-items: center;
    gap: 7px;
    color: #93a0b2;
}

.beta-create-dialog__progress li:not(:last-child)::after {
    height: 1px;
    flex: 1;
    margin: 0 10px;
    background: #dfe5ed;
    content: '';
}

.beta-create-dialog__progress li.is-complete::after {
    background: color-mix(in srgb, var(--main-color) 40%, #dfe5ed);
}

.beta-create-dialog__progress li > span {
    display: grid;
    width: 24px;
    height: 24px;
    flex: none;
    place-items: center;
    border: 1px solid #d8e0ea;
    border-radius: 50%;
    background: #fff;
    font-size: 9px;
    font-weight: 700;
}

.beta-create-dialog__progress li > span svg {
    width: 13px;
    height: 13px;
    stroke: currentColor;
    stroke-width: 1.8;
    stroke-linecap: round;
    stroke-linejoin: round;
}

.beta-create-dialog__progress li.is-active,
.beta-create-dialog__progress li.is-complete {
    color: var(--main-color);
}

.beta-create-dialog__progress li.is-active > span,
.beta-create-dialog__progress li.is-complete > span {
    border-color: var(--main-color);
    background: var(--main-color);
    color: #fff;
}

.beta-create-dialog__progress li small {
    flex: none;
    font-size: 12px;
    font-weight: 700;
    white-space: nowrap;
}

.beta-create-dialog__body {
    min-height: 0;
    overflow-y: auto;
}

.beta-create-dialog__type-tabs {
    position: sticky;
    z-index: 2;
    top: 0;
    display: flex;
    overflow-x: auto;
    border-bottom: 1px solid #e8edf3;
    background: rgba(255, 255, 255, 0.98);
    padding: 0 20px;
    scrollbar-width: thin;
}

.beta-create-dialog__type-tab {
    position: relative;
    display: grid;
    min-width: 112px;
    min-height: 68px;
    flex: 1 0 auto;
    grid-template-columns: 20px minmax(0, auto);
    align-content: center;
    align-items: center;
    justify-content: center;
    gap: 3px 7px;
    border-bottom: 2px solid transparent;
    color: #8795a8;
    text-align: left;
}

.beta-create-dialog__type-tab svg {
    display: block;
    width: 20px;
    height: 20px;
    grid-row: 1;
    stroke: currentColor;
    stroke-width: 1.6;
    stroke-linecap: round;
    stroke-linejoin: round;
}

.beta-create-dialog__type-tab span {
    grid-column: 2;
    grid-row: 1;
    font-size: 12px;
    font-weight: 700;
    line-height: 20px;
    white-space: nowrap;
}

.beta-create-dialog__type-tab small {
    grid-column: 2;
    color: #9d7b45;
    font-size: 7px;
    font-weight: 700;
}

.beta-create-dialog__type-tab:hover:not(:disabled),
.beta-create-dialog__type-tab.is-active {
    border-bottom-color: var(--main-color);
    color: var(--main-color);
}

.beta-create-dialog__type-tab:disabled {
    cursor: not-allowed;
    opacity: 0.45;
}

.beta-create-dialog__stage {
    padding: 24px 28px 30px;
}

.beta-create-dialog__stage-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 24px;
}

.beta-create-dialog__stage-header > div > small {
    color: var(--main-color);
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.13em;
    text-transform: uppercase;
}

.beta-create-dialog__stage-header h3 {
    margin-top: 3px;
    color: #142844;
    font-size: 21px;
    font-weight: 700;
    letter-spacing: -0.025em;
}

.beta-create-dialog__stage-header p {
    margin-top: 5px;
    color: #72839a;
    font-size: 11px;
    line-height: 1.5;
}

.beta-create-dialog__selected-kind {
    flex: none;
    border: 1px solid color-mix(in srgb, var(--main-color) 22%, #e2e8f0);
    border-radius: 999px;
    background: color-mix(in srgb, var(--main-color) 7%, white);
    padding: 7px 11px;
    color: var(--main-color);
    font-size: 12px;
    font-weight: 700;
}

.beta-create-dialog__type-guide {
    display: grid;
    min-height: 310px;
    grid-template-columns: minmax(0, 0.9fr) minmax(360px, 1.1fr);
    overflow: hidden;
    margin-top: 20px;
    border: 1px solid #dfe6ef;
    border-radius: 20px;
}

.beta-create-dialog__type-guide-copy {
    display: grid;
    grid-template-columns: 46px minmax(0, 1fr);
    align-content: center;
    gap: 0 14px;
    padding: 30px;
}

.beta-create-dialog__type-guide-copy > div > small {
    color: var(--main-color);
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
}

.beta-create-dialog__type-guide-copy h4 {
    margin-top: 4px;
    color: #18304e;
    font-size: 20px;
    font-weight: 700;
}

.beta-create-dialog__type-guide-copy p {
    max-width: 440px;
    margin-top: 7px;
    color: #697c95;
    font-size: 11px;
    line-height: 1.55;
}

.beta-create-dialog__type-guide-copy dl {
    display: grid;
    grid-column: 1 / -1;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
    margin-top: 26px;
}

.beta-create-dialog__type-guide-copy dl > div {
    border: 1px solid #e3e9f1;
    border-radius: 14px;
    background: #fafbfd;
    padding: 12px;
}

.beta-create-dialog__type-guide-copy dt {
    color: #8b99ab;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
}

.beta-create-dialog__type-guide-copy dd {
    margin-top: 5px;
    color: #405775;
    font-size: 10px;
    line-height: 1.5;
}

.beta-create-dialog__concept-preview {
    position: relative;
    overflow: hidden;
    border-left: 1px solid color-mix(in srgb, var(--main-color) 14%, #e4e9f0);
    background: color-mix(in srgb, var(--main-color) 6%, #f4f7fa);
    padding: 42px 32px 28px;
}

.beta-create-dialog__concept-preview::before,
.beta-create-dialog__concept-preview::after {
    position: absolute;
    border-radius: 50%;
    background: color-mix(in srgb, var(--main-color) 9%, transparent);
    content: '';
}

.beta-create-dialog__concept-preview::before {
    top: -90px;
    right: -50px;
    width: 230px;
    height: 230px;
}

.beta-create-dialog__concept-preview::after {
    bottom: -95px;
    left: -40px;
    width: 190px;
    height: 190px;
}

.beta-create-dialog__concept-preview > * {
    position: relative;
    z-index: 1;
}

.beta-create-dialog__browser-bar {
    display: flex;
    height: 28px;
    align-items: center;
    gap: 5px;
    border: 1px solid #dce4ed;
    border-bottom: 0;
    border-radius: 13px 13px 0 0;
    background: #f8fafc;
    padding: 0 10px;
}

.beta-create-dialog__browser-bar i {
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: #c5cfdb;
}

.beta-create-dialog__browser-bar span {
    width: 42%;
    height: 6px;
    margin-left: auto;
    margin-right: auto;
    border-radius: 999px;
    background: #e2e8ef;
}

.beta-create-dialog__concept-preview {
    color: color-mix(in srgb, var(--main-color) 34%, #98a6b8);
}

.beta-create-dialog__browser-bar ~ *:not(.beta-create-dialog__mock-link) {
    border-right: 1px solid #dce4ed;
    border-left: 1px solid #dce4ed;
    background-color: #fff;
}

.beta-create-dialog__mock-title {
    display: block;
    width: 100%;
    height: 46px;
    padding: 18px 24px;
}

.beta-create-dialog__mock-title::before {
    display: block;
    width: 32%;
    height: 9px;
    border-radius: 999px;
    background: currentColor;
    content: '';
    opacity: 0.55;
}

.beta-create-dialog__mock-grid,
.beta-create-dialog__mock-gallery,
.beta-create-dialog__mock-quotes {
    display: grid;
    min-height: 166px;
    gap: 8px;
    border-bottom: 1px solid #dce4ed;
    padding: 0 24px 22px;
}

.beta-create-dialog__mock-grid {
    grid-template-columns: repeat(3, 1fr);
}

.beta-create-dialog__mock-grid i,
.beta-create-dialog__mock-gallery i {
    border-radius: 6px;
    background: linear-gradient(145deg, currentColor, color-mix(in srgb, currentColor 32%, white));
    opacity: 0.42;
}

.beta-create-dialog__mock-gallery {
    grid-template-columns: 1.2fr 0.8fr 0.8fr;
    grid-template-rows: repeat(2, 1fr);
}

.beta-create-dialog__mock-gallery i:first-child {
    grid-row: 1 / span 2;
}

.beta-create-dialog__mock-form {
    display: grid;
    min-height: 166px;
    align-content: center;
    gap: 10px;
    border-bottom: 1px solid #dce4ed;
    padding: 16px 56px 26px;
}

.beta-create-dialog__mock-form i {
    height: 17px;
    border: 1px solid #dfe5ec;
    border-radius: 5px;
    background: #f8fafc;
}

.beta-create-dialog__mock-form b {
    width: 38%;
    height: 22px;
    justify-self: end;
    border-radius: 6px;
    background: currentColor;
    opacity: 0.65;
}

.beta-create-dialog__mock-quotes {
    grid-template-columns: repeat(3, 1fr);
}

.beta-create-dialog__mock-quotes i {
    position: relative;
    border: 1px solid #e0e6ee;
    border-radius: 9px;
    background: #fafbfd;
}

.beta-create-dialog__mock-quotes i::before {
    position: absolute;
    top: 18px;
    right: 13px;
    left: 13px;
    height: 35px;
    background: repeating-linear-gradient(#dfe5ec 0 3px, transparent 3px 10px);
    content: '';
}

.beta-create-dialog__mock-about,
.beta-create-dialog__mock-hero {
    display: grid;
    min-height: 212px;
    grid-template-columns: 0.9fr 1.1fr;
    align-items: center;
    gap: 24px;
    border-bottom: 1px solid #dce4ed;
    padding: 28px;
}

.beta-create-dialog__mock-about > i,
.beta-create-dialog__mock-hero > i {
    height: 130px;
    border-radius: 10px;
    background: linear-gradient(145deg, currentColor, color-mix(in srgb, currentColor 28%, white));
    opacity: 0.42;
}

.beta-create-dialog__mock-about span,
.beta-create-dialog__mock-hero span,
.beta-create-dialog__mock-copy {
    display: grid;
    gap: 11px;
}

.beta-create-dialog__mock-about b,
.beta-create-dialog__mock-hero b,
.beta-create-dialog__mock-copy i {
    height: 7px;
    border-radius: 999px;
    background: #dce3eb;
}

.beta-create-dialog__mock-about b:first-child,
.beta-create-dialog__mock-hero b:first-child {
    width: 65%;
    height: 12px;
    background: currentColor;
    opacity: 0.58;
}

.beta-create-dialog__mock-hero {
    min-height: 158px;
    border-bottom: 0;
    padding-bottom: 12px;
}

.beta-create-dialog__mock-hero > i {
    height: 105px;
}

.beta-create-dialog__mock-copy {
    min-height: 54px;
    align-content: start;
    border-bottom: 1px solid #dce4ed;
    padding: 8px 28px 18px;
}

.beta-create-dialog__mock-link {
    display: grid;
    min-height: 212px;
    place-items: center;
    border: 1px solid #dce4ed;
    border-top: 0;
    background: #fff;
}

.beta-create-dialog__mock-link svg {
    width: 62px;
    height: 62px;
    stroke: currentColor;
    stroke-width: 1;
    stroke-linecap: round;
    stroke-linejoin: round;
}

.beta-create-dialog__mock-link span {
    width: 34%;
    height: 8px;
    margin-top: -60px;
    border-radius: 999px;
    background: #dfe5ec;
}

.beta-create-dialog__layout-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 14px;
    margin-top: 20px;
}

.beta-create-dialog__layout {
    overflow: hidden;
    border: 2px solid #e0e6ee;
    border-radius: 17px;
    background: #fff;
    text-align: left;
    transition: border-color 160ms ease, transform 160ms ease;
}

.beta-create-dialog__layout:hover,
.beta-create-dialog__layout.is-active {
    border-color: var(--main-color);
}

.beta-create-dialog__layout:hover {
    transform: translateY(-2px);
}

.beta-create-dialog__layout-preview {
    position: relative;
    display: grid;
    height: 190px;
    overflow: hidden;
    place-items: center;
    border-bottom: 1px solid #e6ebf1;
    background: #f2f5f8;
}

.beta-create-dialog__layout-preview img {
    width: 100%;
    height: 100%;
    object-fit: contain;
}

.beta-create-dialog__layout-placeholder {
    display: grid;
    width: 78%;
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
}

.beta-create-dialog__layout-placeholder i {
    height: 54px;
    border-radius: 7px;
    background: #dfe6ed;
}

.beta-create-dialog__layout-check {
    position: absolute;
    top: 12px;
    right: 12px;
    display: grid;
    width: 28px;
    height: 28px;
    place-items: center;
    border: 3px solid #fff;
    border-radius: 50%;
    background: var(--main-color);
    color: #fff;
}

.beta-create-dialog__layout-check svg {
    width: 15px;
    height: 15px;
    stroke: currentColor;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
}

.beta-create-dialog__layout-copy {
    display: flex;
    min-height: 64px;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 10px 12px;
}

.beta-create-dialog__layout-copy span {
    min-width: 0;
}

.beta-create-dialog__layout-copy small,
.beta-create-dialog__layout-copy strong {
    display: block;
}

.beta-create-dialog__layout-copy small {
    color: #8a98aa;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
}

.beta-create-dialog__layout-copy strong {
    overflow: hidden;
    margin-top: 3px;
    color: #304761;
    font-size: 10px;
    font-weight: 700;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.beta-create-dialog__layout-copy em {
    flex: none;
    color: var(--main-color);
    font-size: 10px;
    font-style: normal;
    font-weight: 700;
}

.beta-create-dialog__empty-layouts {
    display: grid;
    justify-items: center;
    gap: 5px;
    margin-top: 20px;
    border: 1px dashed #d6dee8;
    border-radius: 16px;
    padding: 48px 24px;
    color: #7c8da3;
    text-align: center;
}

.beta-create-dialog__empty-layouts > span {
    display: grid;
    width: 34px;
    height: 34px;
    place-items: center;
    border-radius: 50%;
    background: #eef2f6;
    font-size: 13px;
    font-weight: 700;
}

.beta-create-dialog__empty-layouts strong {
    margin-top: 5px;
    color: #536983;
    font-size: 11px;
}

.beta-create-dialog__empty-layouts small {
    font-size: 9px;
}

.beta-create-dialog__details {
    display: grid;
    grid-template-columns: minmax(0, 1.45fr) minmax(280px, 0.75fr);
    gap: 18px;
    margin-top: 20px;
}

.beta-create-dialog__details-form,
.beta-create-dialog__summary {
    border: 1px solid #dfe6ef;
    border-radius: 18px;
    padding: 22px;
}

.beta-create-dialog__details-form {
    display: grid;
    align-content: start;
    gap: 18px;
}

.beta-create-dialog__field {
    display: grid;
    gap: 8px;
}

.beta-create-dialog__field > span {
    color: #334b69;
    font-size: 12px;
    font-weight: 700;
}

.beta-create-dialog__field input {
    width: 100%;
    border: 1px solid #d8e1ec;
    border-radius: 12px;
    padding: 14px 15px;
    color: #142844;
    font-size: 12px;
    outline: none;
}

.beta-create-dialog__field input:focus {
    border-color: var(--main-color);
    outline: 3px solid color-mix(in srgb, var(--main-color) 10%, transparent);
    outline-offset: 1px;
}

.beta-create-dialog__field small {
    color: #8090a5;
    font-size: 10px;
}

.beta-create-dialog__field small.is-error {
    color: #b34e4e;
}

.beta-create-dialog__after-create {
    display: grid;
    grid-template-columns: 22px minmax(0, 1fr);
    gap: 10px;
    border-radius: 13px;
    background: #f4f7fa;
    padding: 13px;
    color: #60758e;
}

.beta-create-dialog__after-create svg {
    width: 20px;
    height: 20px;
    stroke: var(--main-color);
    stroke-width: 1.5;
    stroke-linecap: round;
    stroke-linejoin: round;
}

.beta-create-dialog__after-create strong,
.beta-create-dialog__after-create small {
    display: block;
}

.beta-create-dialog__after-create strong {
    color: #405873;
    font-size: 11px;
    font-weight: 700;
}

.beta-create-dialog__after-create small {
    margin-top: 3px;
    font-size: 10px;
    line-height: 1.5;
}

.beta-create-dialog__summary {
    display: grid;
    align-content: start;
    gap: 10px;
    background: #fafbfd;
}

.beta-create-dialog__summary-label {
    color: #7f8ea2;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
}

.beta-create-dialog__summary-kind,
.beta-create-dialog__summary-layout {
    display: grid;
    min-width: 0;
    grid-template-columns: 42px minmax(0, 1fr);
    align-items: center;
    gap: 10px;
    border-top: 1px solid #e3e9f0;
    padding-top: 10px;
}

.beta-create-dialog__summary-preview {
    display: grid;
    width: 42px;
    height: 42px;
    overflow: hidden;
    place-items: center;
    border-radius: 11px;
    background: #eaf0f5;
    color: #8293a9;
    font-size: 10px;
    font-weight: 700;
}

.beta-create-dialog__summary-preview img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.beta-create-dialog__summary small,
.beta-create-dialog__summary strong {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
}

.beta-create-dialog__summary small {
    color: #8593a6;
    font-size: 11px;
    white-space: nowrap;
}

.beta-create-dialog__summary strong {
    margin-top: 3px;
    color: #304761;
    font-size: 11px;
    font-weight: 700;
    white-space: nowrap;
}

.beta-create-dialog__footer {
    display: flex;
    min-height: 72px;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
    border-top: 1px solid #e8edf3;
    background: #fff;
    padding: 13px 28px;
}

.beta-create-dialog__footer > p {
    color: #8392a6;
    font-size: 12px;
}

.beta-create-dialog__footer > div {
    display: flex;
    align-items: center;
    gap: 8px;
}

.beta-create-dialog__cancel,
.beta-create-dialog__submit {
    display: inline-flex;
    min-height: 42px;
    align-items: center;
    justify-content: center;
    gap: 7px;
    border-radius: var(--beta-button-radius);
    padding: 0 17px;
    font-size: 12px;
    font-weight: 700;
}

.beta-create-dialog__cancel {
    color: #657892;
}

.beta-create-dialog__submit {
    min-width: 126px;
    background: var(--main-color);
    color: #fff;
}

.beta-create-dialog__submit svg {
    width: 14px;
    height: 14px;
    stroke: currentColor;
    stroke-width: 1.8;
    stroke-linecap: round;
    stroke-linejoin: round;
}

.beta-create-dialog__submit:disabled {
    cursor: not-allowed;
    background: #aab6c6;
}

@media (max-width: 900px) {
    .beta-create-dialog__header {
        grid-template-columns: minmax(0, 1fr) auto;
    }

    .beta-create-dialog__header-copy {
        display: none;
    }

    .beta-create-dialog__type-guide {
        grid-template-columns: 1fr;
    }

    .beta-create-dialog__concept-preview {
        min-height: 290px;
        border-top: 1px solid color-mix(in srgb, var(--main-color) 14%, #e4e9f0);
        border-left: 0;
    }

    .beta-create-dialog__layout-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

@media (max-width: 640px) {
    .beta-create-dialog {
        place-items: stretch;
        padding: 8px;
    }

    .beta-create-dialog__panel {
        height: calc(100vh - 16px);
        border-radius: 18px;
    }

    .beta-create-dialog__header {
        padding: 14px 16px;
    }

    .beta-create-dialog__header-icon {
        width: 38px;
        height: 38px;
    }

    .beta-create-dialog__header-title h2 {
        font-size: 17px;
    }

    .beta-create-dialog__progress {
        padding: 10px 16px;
    }

    .beta-create-dialog__progress li {
        flex: 0 1 auto;
    }

    .beta-create-dialog__progress li:not(:last-child)::after {
        width: 22px;
        flex: none;
        margin: 0 5px;
    }

    .beta-create-dialog__progress li small {
        display: none;
    }

    .beta-create-dialog__type-tabs {
        padding: 0 8px;
    }

    .beta-create-dialog__type-tab {
        min-width: 104px;
        min-height: 60px;
    }

    .beta-create-dialog__stage {
        padding: 20px 16px 24px;
    }

    .beta-create-dialog__stage-header {
        display: block;
    }

    .beta-create-dialog__selected-kind {
        display: inline-block;
        margin-top: 10px;
    }

    .beta-create-dialog__type-guide-copy {
        padding: 22px 18px;
    }

    .beta-create-dialog__type-guide-copy dl,
    .beta-create-dialog__layout-grid,
    .beta-create-dialog__details {
        grid-template-columns: 1fr;
    }

    .beta-create-dialog__concept-preview {
        display: none;
    }

    .beta-create-dialog__layout-preview {
        height: 180px;
    }

    .beta-create-dialog__footer {
        min-height: 68px;
        padding: 11px 16px;
    }

    .beta-create-dialog__footer > p {
        display: none;
    }

    .beta-create-dialog__footer > div {
        width: 100%;
        justify-content: flex-end;
    }
}
</style>
